import * as DateFNS from 'date-fns';
import React, { Fragment, useMemo, useRef, useState } from 'react';
import { Box, Flex, PopoverV2, styled, usePopoverContext, useTheme } from '@withjoy/joykit';
import { ChevronLeftSquare, ChevronRightSquare } from '@withjoy/joykit/icons';
import { notNullOrUndefined } from '@shared/utils/notNullOrUndefined';
import { createPortal } from 'react-dom';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type DayOfTheWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

type CalendarRangePickerProps = Readonly<{
  appendPickerToBody?: boolean;
  children: JSX.Element;
  onChange: (value: DateRangeValues) => void;
  initial: DateRangeValues;
  isDisabled?: boolean;
  closeOnSelectDate?: boolean;
  firstEdit?: 'start' | 'end';
}>;

export const CalendarRangePicker = (props: CalendarRangePickerProps) => {
  return (
    <PopoverV2 placement={'auto'}>
      <CalendarRangePickerRoot {...props} />
    </PopoverV2>
  );
};

const CalendarRangePickerRoot = ({ appendPickerToBody, children, onChange, initial, isDisabled, closeOnSelectDate, firstEdit }: CalendarRangePickerProps) => {
  const { closePopover } = usePopoverContext();
  const handleOnChange = useEventCallback(([start, end]: Readonly<[Date | undefined, Date | undefined]>) => {
    if (start && end && closeOnSelectDate) {
      closePopover();
    }
    if (start && end) {
      onChange([start, end]);
    }
  });
  return (
    <>
      <PopoverV2.Trigger disabled={isDisabled}>{children}</PopoverV2.Trigger>
      {!isDisabled ? (
        <>
          {appendPickerToBody ? (
            createPortal(
              <PopoverV2.Content marginX={8}>
                <DateRange
                  onChange={handleOnChange}
                  initial={initial}
                  firstEdit={firstEdit}
                />
              </PopoverV2.Content>,
              document.body,
            )
          ) : (
            <PopoverV2.Content marginX={8}>
              <DateRange
                onChange={handleOnChange}
                initial={initial}
                firstEdit={firstEdit}
              />
            </PopoverV2.Content>
          )}
        </>
      ) : null}
    </>
  );
};

export function MonthViewMiniTitleForRange({
  calendarDate,
  handleMoveForward,
  handleMoveBackwards,
}: {
  calendarDate: Date;
  handleMoveForward?: () => void;
  handleMoveBackwards?: () => void;
}) {
  return (
    <Box
      display={'flex'}
      position={'relative'}
      width={'full'}
      alignItems={'center'}
      height={'2.5rem'}
    >
      {handleMoveBackwards && (
        <Box
          as="button"
          type="button"
          position={'absolute'}
          left={0}
          marginRight={'auto'}
          marginY={'-0.25rem'}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          padding={'0.375rem'}
          onClick={handleMoveBackwards}
          aria-label={'Previous Month'}
        >
          <ChevronLeftSquare aria-hidden="true" />
        </Box>
      )}
      <Box
        typographyVariant={'hed2'}
        flex={'1 1 auto'}
        textAlign={'center'}
        color={'mono12'}
        className="jk-t-hed-2 flex-auto text-center text-gray-900"
      >
        {DateFNS.format(calendarDate, 'MMMM yyyy')}
      </Box>
      {handleMoveForward && (
        <Box
          as="button"
          type="button"
          position={'absolute'}
          right={0}
          marginLeft={'auto'}
          marginY={'-0.25rem'}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          padding={'0.375rem'}
          aria-label={'Next Month'}
          onClick={handleMoveForward}
        >
          <ChevronRightSquare aria-hidden="true" />
        </Box>
      )}
    </Box>
  );
}

const sortDayAsc = (a: Date, b: Date) => {
  if (DateFNS.isBefore(a, b)) {
    return -1;
  }
  if (DateFNS.isAfter(a, b)) {
    return 1;
  }
  return 0;
};

interface Props {
  calendarDate: Date;
  activeDates: Date[];
  allowableDaysOfTheWeek?: {
    [key in DayOfTheWeek]: boolean;
  };
  handleSelectActiveDate: (date: Date) => void;
  handleMoveForward?: () => void;
  handleMoveBackwards?: () => void;
  mode: 'specific' | 'range';
}

export function MonthViewMini({
  calendarDate,
  activeDates,
  handleSelectActiveDate,
  handleMoveForward,
  handleMoveBackwards,
  allowableDaysOfTheWeek = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
  },
  mode,
}: Props) {
  const richDays = richDaysInMonthMulti(calendarDate, activeDates);
  const firstSelectedDate = activeDates.sort(sortDayAsc)[0];
  const lastSelectedDate = activeDates.sort(sortDayAsc)[activeDates.length - 1];

  return (
    <div className="">
      <MonthViewMiniTitleForRange
        {...{
          calendarDate,
          handleMoveForward,
          handleMoveBackwards,
        }}
      />
      <Calendar
        {...{
          mode,
          richDays,
          handleSelectActiveDate,
          firstSelectedDate,
          lastSelectedDate,
          allowableDaysOfTheWeek,
        }}
      />
    </div>
  );
}

/**
 * Get all the days in the current month
 * @param day day in the current month
 * @returns
 */
export const daysInMonth = (day: Date) => {
  // const startOfMonth = day.startOf("month");
  // const endOfMonth = day.endOf("month");
  const startOfMonth = DateFNS.startOfMonth(day);
  const endOfMonth = DateFNS.endOfMonth(day);
  // const startOfFirstMonthWeek = startOfMonth.startOf("w");
  // const endOfLastMonthWeek = endOfMonth.endOf("w").subtract(1, "day");
  const startOfFirstMonthWeek = DateFNS.startOfWeek(startOfMonth);
  const endOfLastMonthWeek = DateFNS.endOfWeek(endOfMonth);
  let date = startOfFirstMonthWeek;
  const weeks = [];
  // while (date.isBefore(endOfLastMonthWeek)) {
  while (DateFNS.isBefore(date, endOfLastMonthWeek)) {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      days.push(date);
      // date = date.add(1, "day");
      date = DateFNS.addDays(date, 1);
    }
    weeks.push(days);
  }
  return weeks;
};

/**
 *
 * @param calendarDay day in the current month
 * @param activeDay current selected day
 * @param availableDays days available to select
 * @returns
 */
export const richDaysInMonth = (calendarDay: Date, activeDay?: Date, availableDays?: Date[]) => {
  const monthDays = daysInMonth(calendarDay);
  return monthDays.flat().map(date => ({
    date,
    // dateString: date.format("YYYY-MM-DD"),
    dateString: DateFNS.format(date, 'YYY-MM-DD'),
    // isCurrentMonth: date.isSame(calendarDay, "month"),
    isCurrentMonth: DateFNS.isSameMonth(date, calendarDay),
    // isToday: date.isSame(dayjs(), "day"),
    isToday: DateFNS.isToday(date),
    // isSelected: date.isSame(activeDay, "day") ?? false,
    isSelected: (activeDay && DateFNS.isSameDay(date, activeDay)) ?? false,
    isDisabled:
      // date.isBefore(dayjs(), "day") ||
      // !availableDays?.some((day) => date.isSame(day, "day")) ||
      // false,
      DateFNS.isBefore(date, new Date()) || !availableDays?.some(day => DateFNS.isSameDay(date, day)) || false,
    hidden: false,
    events: [],
  }));
};

/**
 * All the days are available to select for a multi select calendar
 * @param calendarDay day in the current month
 * @param activeDays current selected days
 * @returns
 */
export const richDaysInMonthMulti = (calendarDay: Date, activeDays?: Date[]) => {
  const monthDays = daysInMonth(calendarDay);
  return monthDays.flat().map(date => ({
    date,
    // dateString: date.format("YYYY-MM-DD"),
    dateString: DateFNS.format(date, 'yyyy-mm-dd'),
    // isCurrentMonth: date.isSame(calendarDay, "month"),
    isCurrentMonth: DateFNS.isSameMonth(date, calendarDay),
    // isToday: date.isSame(dayjs(), "day"),
    isToday: DateFNS.isSameDay(date, new Date()),
    // isSelected: activeDays?.some((day) => date.isSame(day, "day")) ?? false,
    isSelected: activeDays?.some(day => DateFNS.isSameDay(date, day)) ?? false,
    isDisabled: DateFNS.isBefore(date, DateFNS.startOfDay(new Date())),
    // hidden: !date.isSame(calendarDay, "month"),
    hidden: !DateFNS.isSameMonth(date, calendarDay),
    events: [],
  }));
};

const CalendarDayButton = styled.button`
  position: relative;
  z-index: 10;
  background-color: white;
  height: 100%;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.mono1};
  }
  &:active:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.mono2};
  }
`;
const Calendar = ({
  mode,
  richDays,
  handleSelectActiveDate,
  firstSelectedDate,
  lastSelectedDate,
  allowableDaysOfTheWeek,
}: {
  mode: 'specific' | 'range';
  richDays: ReturnType<typeof richDaysInMonthMulti>;
  handleSelectActiveDate: (date: Date) => void;
  firstSelectedDate: Date;
  lastSelectedDate?: Date;
  allowableDaysOfTheWeek: {
    [key in DayOfTheWeek]: boolean;
  };
}) => {
  const theme = useTheme();
  return (
    <>
      <Box
        typographyVariant={'label1'}
        marginTop={'1.5rem'}
        display={'grid'}
        gridTemplateColumns={'repeat(7,40px)'}
        justifyContent={'space-between'}
        columnGap={'0.25rem'}
        textAlign={'center'}
        color={'mono5'}
        // className="jk-t-label-3 mt-6 grid w-full grid-cols-[repeat(7,_40px)] justify-between gap-1 text-center"
      >
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          SUN
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          MON
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          TUE
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          WED
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          THU
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          FRI
        </Flex>
        <Flex
          alignItems={'center'}
          justifyContent={'center'}
        >
          SAT
        </Flex>
      </Box>
      {mode === 'specific' ? (
        <Box
          marginTop={'0.5rem'}
          gridTemplateColumns={'repeat(1fr, 7)'}
          gridTemplateRows={'repeat(1fr, 6)'}
          rowGap={'1.5rem'}
          columnGap={'1.5rem'}
          typographyVariant={'label2'}
        >
          {richDays.map(day => (
            <Box key={day.date.toISOString()}>
              {day.hidden ? null : (
                <CalendarDayButton
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    !day.isDisabled && handleSelectActiveDate(day.date);
                  }}
                  disabled={day.isDisabled || !allowableDaysOfTheWeek[DateFNS.format(day.date, 'EEEE').toLowerCase() as DayOfTheWeek]}
                  style={{
                    ...(day.isDisabled && {
                      color: theme.colors.mono7,
                    }),
                    ...(!allowableDaysOfTheWeek[DateFNS.format(day.date, 'EEEE').toLowerCase() as DayOfTheWeek] && {
                      color: theme.colors.mono7,
                    }),
                  }}
                >
                  {/* <time dateTime={day.date.toISOString()}>{day.date.format('D')}</time> */}
                  <time dateTime={day.date.toISOString()}>{DateFNS.format(day.date, 'd')}</time>
                  {day.isToday && (
                    <Box
                      position={'absolute'}
                      bottom={'1rem'}
                      height={'0.25rem'}
                      width={'0.25rem'}
                      borderRadius={'99999px'}
                      color={day.isSelected ? 'white' : 'mono14'}
                    />
                  )}
                </CalendarDayButton>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          marginTop={'0.5rem'}
          display={'grid'}
          gridTemplateColumns={['2.5rem 1fr 2.5rem 1fr 2.5rem 1fr 2.5rem 1fr 2.5rem 1fr 2.5rem 1fr 2.5rem', '3rem 1fr 3rem 1fr 3rem 1fr 3rem 1fr 3rem 1fr 3rem 1fr 3rem']}
          gridTemplateRows={'repeat(6, 1fr)'}
          justifyContent={'space-between'}
          rowGap={'0.5rem'}
          typographyVariant={'label2'}
        >
          {richDays.map((day, index) => {
            const dayIsBetweenSelectedDates = [
              firstSelectedDate,
              lastSelectedDate,
              // day.date.isAfter(firstSelectedDate, 'day'),
              DateFNS.isAfter(DateFNS.startOfDay(day.date), DateFNS.startOfDay(firstSelectedDate)),
              // day.date.isBefore(lastSelectedDate, 'day')
              lastSelectedDate && DateFNS.isBefore(DateFNS.startOfDay(day.date), DateFNS.startOfDay(lastSelectedDate)),
              // all values must be truthy
            ].every(v => !!v);

            // const dayIsFirstSelectedDate = firstSelectedDate && day.date.isSame(firstSelectedDate, 'day');
            const dayIsFirstSelectedDate = firstSelectedDate && DateFNS.isSameDay(day.date, firstSelectedDate);
            // const dayIsLastSelectedDate = lastSelectedDate && day.date.isSame(lastSelectedDate, 'day');
            const dayIsLastSelectedDate = lastSelectedDate && DateFNS.isSameDay(day.date, lastSelectedDate);
            return (
              <Fragment key={day.date.toISOString()}>
                <Box
                  position={'relative'}
                  aspectRatio={'1 / 1'}
                >
                  {day.hidden ? null : (
                    <CalendarDayButton
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        !day.isDisabled && handleSelectActiveDate(day.date);
                      }}
                      disabled={day.isDisabled || !allowableDaysOfTheWeek[DateFNS.format(day.date, 'EEEE').toLowerCase() as DayOfTheWeek]}
                      style={{
                        ...(dayIsFirstSelectedDate && {
                          borderRadius: '9999rem 0 0 9999rem',
                          backgroundColor: theme.colors.newUIBrandPrimary,
                          color: theme.colors.white,
                        }),
                        ...(dayIsLastSelectedDate && {
                          borderRadius: '0 9999rem 9999rem 0',
                          backgroundColor: theme.colors.newUIBrandPrimary,
                          color: theme.colors.white,
                        }),
                        ...(dayIsFirstSelectedDate &&
                          dayIsLastSelectedDate && {
                            borderRadius: '9999rem',
                          }),
                        ...(dayIsBetweenSelectedDates && {
                          backgroundColor: theme.colors.primaryWeak,
                          color: theme.colors.mono14,
                        }),
                        ...(day.isDisabled && {
                          color: theme.colors.mono7,
                        }),
                        ...(!allowableDaysOfTheWeek[DateFNS.format(day.date, 'EEEE').toLowerCase() as DayOfTheWeek] && {
                          color: theme.colors.mono7,
                        }),
                      }}
                    >
                      {/* <time dateTime={day.date.toISOString()}>{day.date.format('D')}</time> */}
                      <time dateTime={day.date.toISOString()}>{DateFNS.format(day.date, 'd')}</time>
                      {day.isToday && (
                        <Box
                          // className={cn(
                          //   'absolute bottom-2 left-0 right-0 m-auto h-1 w-1 rounded-full bg-mono-140 min-[480px]:bottom-[0.625rem]',
                          //   day.isSelected && 'bg-white',
                          //   day.isDisabled && day.isSelected && 'bg-mono-140'
                          // )}
                          position={'absolute'}
                          bottom={['0.625rem', '0.125rem']}
                          left={0}
                          right={0}
                          margin="auto"
                          height={'0.25rem'}
                          width={'0.25rem'}
                          borderRadius={'9999rem'}
                          backgroundColor={'mono14'}
                        />
                      )}
                    </CalendarDayButton>
                  )}
                </Box>
                {(index + 1) % 7 !== 0 && (
                  // <Box className={((dayIsFirstSelectedDate || dayIsBetweenSelectedDates) && !dayIsLastSelectedDate && !day.hidden && 'bg-mono-20') || ''} />
                  <Box backgroundColor={((dayIsFirstSelectedDate || dayIsBetweenSelectedDates) && !dayIsLastSelectedDate && !day.hidden && 'primaryWeak') || 'transparent'} />
                )}
              </Fragment>
            );
          })}
        </Box>
      )}
    </>
  );
};

type DateRangeValues = Readonly<[Date | undefined, Date | undefined]>;

type DateRangeProps = Readonly<{
  initial: DateRangeValues;
  onChange?: (value: DateRangeValues) => void;
  /**
   * Controls which boundary is edited first when the component mounts with both dates already selected.
   * 'end'  -> first click edits end (keeps start unless reordering needed due to earlier pick)
   * 'start'-> first click edits start
   * undefined -> legacy behavior: first click after both selected resets and starts a new range
   */
  firstEdit?: 'start' | 'end';
}>;

export const DateRange = ({ initial, onChange, firstEdit }: DateRangeProps) => {
  const [activeDates, setActiveDates] = useState<DateRangeValues>(initial);
  // set calendar date to first date in range if range is selected
  const [calendarDate, setCalendarDate] = useState<Date>(initial[0] || new Date());
  const calendarDateSecondMonth = DateFNS.addMonths(calendarDate, 1);
  const firstEditConsumedRef = React.useRef<boolean>(false);
  const secondEditConsumedRef = React.useRef<boolean>(false);
  const lastDateChanged = useRef<Date | undefined>(undefined);

  const handleMoveBackwards = () => {
    setCalendarDate(prev => DateFNS.subMonths(prev, 1));
  };

  const handleMoveForward = () => {
    setCalendarDate(prev => DateFNS.addMonths(prev, 1));
  };

  const updateActiveDates = (dates: DateRangeValues) => {
    setActiveDates(dates);
    onChange && onChange(dates);
  };

  /**
   * Orders two dates in ascending order.
   * @param a - The first date.
   * @param b - The second date.
   * @returns A tuple containing the two dates in ascending order as a readonly array.
   */
  const orderRange = (a: Date, b: Date): Readonly<DateRangeValues> => (DateFNS.isBefore(a, b) ? [a, b] : [b, a]);

  const handleSelectActiveDate = (date: Date) => {
    const [start, end] = activeDates;
    const hasEditedOnce = firstEditConsumedRef.current;
    const hasEditedTwice = secondEditConsumedRef.current;
    firstEditConsumedRef.current = true;
    if (firstEdit === 'end' && !hasEditedOnce) {
      if (start) {
        lastDateChanged.current = date;
        updateActiveDates(orderRange(date, start));
        return;
      }
    } else if (firstEdit === 'start' && !hasEditedOnce) {
      if (end) {
        lastDateChanged.current = date;
        updateActiveDates(orderRange(end, date));
        return;
      }
    } else if (!hasEditedTwice && lastDateChanged.current) {
      secondEditConsumedRef.current = true;
      if (start && end) {
        const dateToKeep = date < lastDateChanged.current ? end : start;
        updateActiveDates(orderRange(dateToKeep, date));
        return;
      }
    }

    if ((start && end) || (!start && !end)) {
      updateActiveDates([date, undefined]);
    } else if (start) {
      updateActiveDates(orderRange(date, start));
    } else if (end) {
      updateActiveDates(orderRange(end, date));
    }
  };

  const dates = useMemo(() => {
    return [...activeDates].filter(notNullOrUndefined);
  }, [activeDates]);

  return (
    <Box
      display={'grid'}
      gridTemplateColumns={['1fr', '1fr', '1fr 1fr']}
      columnGap={'1rem'}
      padding={[0, 0, 9]}
    >
      {/* wider screen - left */}
      <Box display={['none', 'none', 'block']}>
        <MonthViewMini
          activeDates={dates}
          calendarDate={calendarDate}
          handleMoveBackwards={handleMoveBackwards}
          handleSelectActiveDate={handleSelectActiveDate}
          allowableDaysOfTheWeek={{
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          }}
          mode={'range'}
        />
      </Box>
      {/* wider screen - right */}
      <Box display={['none', 'none', 'block']}>
        <MonthViewMini
          activeDates={dates}
          calendarDate={calendarDateSecondMonth}
          handleMoveForward={handleMoveForward}
          handleSelectActiveDate={handleSelectActiveDate}
          allowableDaysOfTheWeek={{
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          }}
          mode={'range'}
        />
      </Box>
      {/* mobile screen */}
      <Box display={['block', 'block', 'none']}>
        <MonthViewMini
          activeDates={dates}
          calendarDate={calendarDate}
          handleMoveBackwards={handleMoveBackwards}
          handleMoveForward={handleMoveForward}
          handleSelectActiveDate={handleSelectActiveDate}
          allowableDaysOfTheWeek={{
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          }}
          mode={'range'}
        />
      </Box>
    </Box>
  );
};
